import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Eye, RefreshCw, X } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, StatusBadge, Btn, Select, formatDate } from './shared';
import { useToast } from '../../hooks/use-toast';

const STATUSES = ['all', 'pending', 'approved', 'shipped', 'cancelled'];
const TYPES = ['all', 'did', 'tollfree', 'uifn'];

export default function AdminOrders() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [ntype, setNtype] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (ntype !== 'all') params.number_type = ntype;
      const res = await api.get('/api/admin/orders', { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [status, ntype, toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, s) => {
    try {
      await api.patch(`/api/admin/orders/${id}`, { status: s });
      toast({ title: 'Status updated' });
      setSelected((o) => o && o.id === id ? { ...o, status: s } : o);
      load();
    } catch (err) { toast({ title: 'Failed', description: formatApiError(err) }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await api.delete(`/api/admin/orders/${id}`);
      toast({ title: 'Deleted' });
      setSelected(null); load();
    } catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  return (
    <>
      <PageHeader
        title="Number Orders"
        subtitle="Virtual Number (DID / Toll-Free / UIFN) purchase requests"
        action={<Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>}
      />

      <AdminCard>
        <div className="flex flex-col md:flex-row gap-2 mb-4">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="md:w-40">
            {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </Select>
          <Select value={ntype} onChange={(e) => setNtype(e.target.value)} className="md:w-40">
            {TYPES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </Select>
        </div>

        {loading ? <Loading /> : items.length === 0 ? <Empty>No orders yet.</Empty> : (
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2">Company</th>
                  <th className="py-2 hidden md:table-cell">Type</th>
                  <th className="py-2 hidden md:table-cell">Country</th>
                  <th className="py-2 hidden lg:table-cell">Date</th>
                  <th className="py-2">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {items.map((o) => (
                  <tr key={o.id} className="hover:bg-white/5" data-testid={`order-row-${o.id}`}>
                    <td className="py-3 font-semibold text-white">{o.company_name}<br /><span className="text-xs text-slate-500">{o.email}</span></td>
                    <td className="py-3 text-slate-300 hidden md:table-cell uppercase text-xs">{o.number_type}</td>
                    <td className="py-3 text-slate-300 hidden md:table-cell">{o.country_name || o.country_code}</td>
                    <td className="py-3 text-slate-400 hidden lg:table-cell">{formatDate(o.created_at)}</td>
                    <td className="py-3"><StatusBadge status={o.status} /></td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Btn size="sm" variant="ghost" onClick={() => setSelected(o)}><Eye size={12} /> View</Btn>
                        <Btn size="sm" variant="danger" onClick={() => remove(o.id)}><Trash2 size={12} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {selected && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setSelected(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">Order detail</h3>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <section className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Type', (selected.number_type || '').toUpperCase()],
                ['Country', `${selected.country_name || ''} ${selected.country_code ? `(${selected.country_code})` : ''}`.trim()],
                ['Package', selected.package_name], ['Monthly', selected.monthly_fee],
                ['Setup', selected.setup_fee], ['Channels', selected.channels],
                ['Company', selected.company_name], ['Contact', selected.contact_name],
                ['Email', selected.email], ['Phone', selected.phone],
                ['VAT', selected.vat_number], ['Address', selected.address],
              ].map(([k, v]) => v ? (
                <div key={k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{k}</p>
                  <p className="text-white mt-0.5 text-sm break-words">{v}</p>
                </div>
              ) : null)}
            </section>
            {selected.kyc_notes && (
              <div className="mt-4">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">KYC notes</p>
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{selected.kyc_notes}</p>
              </div>
            )}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
              <div className="flex flex-wrap gap-2">
                {['pending', 'approved', 'shipped', 'cancelled'].map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase border ${
                      selected.status === s
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-transparent'
                        : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                    }`}
                  >{s}</button>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Btn variant="danger" onClick={() => remove(selected.id)}><Trash2 size={14} /> Delete</Btn>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
